import { type FlyerContent, WORDMARK_SRC } from './flyer-content'

const STORY_WIDTH = 1080
const STORY_HEIGHT = 1920

const PAD = 88
const COVER_HEIGHT = 1120
const QR_TILE = 520
const QR_PADDING = 32
const SERIF = "Lora, 'Iowan Old Style', Georgia, serif"
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif"
const MONO = "ui-monospace, Menlo, Consolas, 'Courier New', monospace"
const TITLE_SIZES = [104, 92, 80, 68]
const TITLE_MAX_LINES = 3

interface FlyerColors {
  night: string
  paper: string
  accent: string
}

function readFlyerColors(): FlyerColors {
  const styles = getComputedStyle(document.documentElement)
  return {
    night: styles.getPropertyValue('--flyer-night').trim(),
    paper: styles.getPropertyValue('--flyer-paper').trim(),
    accent: styles.getPropertyValue('--flyer-accent').trim(),
  }
}

function loadImage(src: string, crossOrigin: boolean): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image()
    if (crossOrigin) image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => resolve(null)
    image.src = src
  })
}

function withCacheBuster(src: string): string {
  const url = new URL(src, window.location.href)
  url.searchParams.set('pulsify-flyer', '1')
  return url.toString()
}

async function loadDrawableCover(src: string): Promise<HTMLCanvasElement | null> {
  // A copy cached from a plain <img> carries no CORS headers, so a second, distinct URL is tried.
  const image = (await loadImage(src, true)) ?? (await loadImage(withCacheBuster(src), true))
  if (!image) return null
  const scratch = document.createElement('canvas')
  scratch.width = STORY_WIDTH
  scratch.height = COVER_HEIGHT
  const ctx = scratch.getContext('2d')
  if (!ctx) return null
  drawCover(ctx, image, STORY_WIDTH, COVER_HEIGHT)
  try {
    ctx.getImageData(0, 0, 1, 1)
    return scratch
  } catch {
    return null
  }
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number,
) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight)
  const drawWidth = image.naturalWidth * scale
  const drawHeight = image.naturalHeight * scale
  ctx.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight)
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = []
  let line = ''
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const candidate = line ? `${line} ${word}` : word
    if (line && ctx.measureText(candidate).width > maxWidth) {
      lines.push(line)
      line = word
    } else {
      line = candidate
    }
  }
  if (line) lines.push(line)
  return lines
}

function clampLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  maxLines: number,
  maxWidth: number,
): string[] {
  if (lines.length <= maxLines) return lines
  const kept = lines.slice(0, maxLines)
  let last = kept[maxLines - 1]
  while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) {
    last = last.slice(0, -1).trimEnd()
  }
  kept[maxLines - 1] = `${last}…`
  return kept
}

function fitTitle(ctx: CanvasRenderingContext2D, title: string, maxWidth: number) {
  for (const size of TITLE_SIZES) {
    ctx.font = `600 ${size}px ${SERIF}`
    const lines = wrapLines(ctx, title, maxWidth)
    if (lines.length <= TITLE_MAX_LINES) return { size, lines }
  }
  const size = TITLE_SIZES[TITLE_SIZES.length - 1]
  ctx.font = `600 ${size}px ${SERIF}`
  return {
    size,
    lines: clampLines(ctx, wrapLines(ctx, title, maxWidth), TITLE_MAX_LINES, maxWidth),
  }
}

function withAlpha(color: string, alpha: number, ctx: CanvasRenderingContext2D): string {
  ctx.fillStyle = color
  const normalized = ctx.fillStyle
  if (normalized.startsWith('#') && normalized.length === 7) {
    const r = Number.parseInt(normalized.slice(1, 3), 16)
    const g = Number.parseInt(normalized.slice(3, 5), 16)
    const b = Number.parseInt(normalized.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
  return normalized
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + width, y, x + width, y + height, radius)
  ctx.arcTo(x + width, y + height, x, y + height, radius)
  ctx.arcTo(x, y + height, x, y, radius)
  ctx.arcTo(x, y, x + width, y, radius)
  ctx.closePath()
}

async function waitForFonts() {
  if (!document.fonts) return
  await Promise.all([
    document.fonts.load(`600 104px ${SERIF}`),
    document.fonts.load(`600 72px ${SERIF}`),
  ]).catch(() => undefined)
}

export async function renderStoryImage(
  content: FlyerContent,
  qrCanvas: HTMLCanvasElement,
): Promise<Blob> {
  const [cover, wordmark] = await Promise.all([
    content.coverUrl ? loadDrawableCover(content.coverUrl) : Promise.resolve(null),
    loadImage(WORDMARK_SRC, false),
    waitForFonts(),
  ])

  const canvas = document.createElement('canvas')
  canvas.width = STORY_WIDTH
  canvas.height = STORY_HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')

  const colors = readFlyerColors()
  const textWidth = STORY_WIDTH - PAD * 2

  ctx.fillStyle = colors.night
  ctx.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT)

  if (cover) {
    ctx.drawImage(cover, 0, 0)
  } else {
    const glow = ctx.createRadialGradient(270, 240, 0, 270, 240, 900)
    glow.addColorStop(0, withAlpha(colors.accent, 0.5, ctx))
    glow.addColorStop(1, withAlpha(colors.accent, 0, ctx))
    ctx.fillStyle = glow
    ctx.fillRect(0, 0, STORY_WIDTH, COVER_HEIGHT)
  }

  const fade = ctx.createLinearGradient(0, 0, 0, COVER_HEIGHT)
  fade.addColorStop(0, withAlpha(colors.night, 0.7, ctx))
  fade.addColorStop(0.2, withAlpha(colors.night, 0.1, ctx))
  fade.addColorStop(0.5, withAlpha(colors.night, 0.6, ctx))
  fade.addColorStop(0.8, withAlpha(colors.night, 0.95, ctx))
  fade.addColorStop(1, withAlpha(colors.night, 1, ctx))
  ctx.fillStyle = fade
  ctx.fillRect(0, 0, STORY_WIDTH, COVER_HEIGHT)

  if (wordmark) {
    const height = 76
    const width = (wordmark.naturalWidth / wordmark.naturalHeight) * height || height * 2.7
    ctx.drawImage(wordmark, PAD, PAD, width, height)
  }

  ctx.textBaseline = 'alphabetic'
  ctx.textAlign = 'center'
  const centerX = STORY_WIDTH / 2

  const codeBaseline = STORY_HEIGHT - 120
  const tileTop = codeBaseline - 150 - QR_TILE
  const tileLeft = centerX - QR_TILE / 2

  ctx.font = `400 40px ${SANS}`
  const taglineLines = wrapLines(ctx, content.tagline, textWidth)
  const taglineLineHeight = 54
  const taglineFirst = tileTop - 64 - (taglineLines.length - 1) * taglineLineHeight
  ctx.fillStyle = withAlpha(colors.paper, 0.8, ctx)
  taglineLines.forEach((line, index) => {
    ctx.fillText(line, centerX, taglineFirst + index * taglineLineHeight)
  })

  ctx.font = `600 72px ${SERIF}`
  const headlineBaseline = taglineFirst - 78
  ctx.fillStyle = colors.paper
  ctx.fillText(content.headline, centerX, headlineBaseline, textWidth)

  ctx.fillStyle = colors.accent
  roundedRect(ctx, centerX - 60, headlineBaseline - 128, 120, 10, 5)
  ctx.fill()

  const metaLineHeight = 54
  const metaLines = [content.when, content.venue].filter(Boolean)
  const metaLast = headlineBaseline - 220
  const metaFirst = metaLast - (metaLines.length - 1) * metaLineHeight
  metaLines.forEach((line, index) => {
    ctx.font = index === 0 ? `500 42px ${SANS}` : `400 42px ${SANS}`
    ctx.fillStyle = index === 0 ? colors.paper : withAlpha(colors.paper, 0.75, ctx)
    ctx.fillText(line, centerX, metaFirst + index * metaLineHeight, textWidth)
  })

  const title = fitTitle(ctx, content.title, textWidth)
  const titleLineHeight = Math.round(title.size * 1.06)
  const titleLast = metaFirst - 64
  const titleFirst = titleLast - (title.lines.length - 1) * titleLineHeight
  ctx.font = `600 ${title.size}px ${SERIF}`
  ctx.fillStyle = colors.paper
  title.lines.forEach((line, index) => {
    ctx.fillText(line, centerX, titleFirst + index * titleLineHeight)
  })

  ctx.fillStyle = colors.paper
  roundedRect(ctx, tileLeft, tileTop, QR_TILE, QR_TILE, 44)
  ctx.fill()
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    qrCanvas,
    tileLeft + QR_PADDING,
    tileTop + QR_PADDING,
    QR_TILE - QR_PADDING * 2,
    QR_TILE - QR_PADDING * 2,
  )
  ctx.imageSmoothingEnabled = true

  ctx.font = `600 30px ${SANS}`
  ctx.fillStyle = colors.accent
  ctx.letterSpacing = '6px'
  if (content.motif === 'promo') {
    ctx.fillText(content.fineprint.toUpperCase(), centerX, codeBaseline - 36)
  } else {
    ctx.fillText(content.codeLabel.toUpperCase(), centerX, codeBaseline - 72)
    ctx.font = `600 84px ${MONO}`
    ctx.fillStyle = colors.paper
    ctx.letterSpacing = '12px'
    ctx.fillText(content.code, centerX, codeBaseline)
  }
  ctx.letterSpacing = '0px'

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Story image export failed'))),
      'image/png',
    )
  })
}
